import { Button, FormHelperText, Grid } from '@mui/material'
import { TFunction } from 'i18next'
import * as yup from 'yup'
import PASSWORD_REGEX, { passwordValidation } from 'constant/passwordRegex'
import { pinSecurityValidation } from 'utilities/pinValidation'
import { useTranslation } from 'react-i18next'
import { SubmitHandler, useForm } from 'react-hook-form'
import OWInput from 'components/OWForm/OWInput'
import WhiteTextField from './WhiteTextField'
import { yupResolver } from '@hookform/resolvers/yup'
import NextButton from './NextButton'
import {
    INewAccountContract,
    useCreateCompanyContext,
} from '../CreateCompanyContext'
import getOverflowSectionSX from '../utils/getOverflowSectionSX'

function AdminAccount() {
    const { t } = useTranslation()
    const { onSubmitAccount, account } = useCreateCompanyContext()
    const validateScheme = (t: TFunction) =>
        yup.object({
            firstName: yup.string().required(),
            lastName: yup.string().required(),
            userName: yup
                .string()
                .matches(/^\S*$/, 'Space is not allowed.')
                .required(),
            email: yup.string().email().required(),
            password: passwordValidation(t),
            confirmPassword: yup
                .string()
                .required(t('user.error.confirm_password_required'))
                .oneOf(
                    [yup.ref('password')],
                    t('user.error.confirm_password_not_identical'),
                ),
            pin: yup
                .string()
                .matches(/^[0-9]+$/, 'Must be only digits')
                .required()
                .length(6)
                .test(
                    'pin-security',
                    t('users.message.pin_setup_security'),
                    (val) => {
                        if (val?.length === 6)
                            return pinSecurityValidation(val ?? '')
                        else return true
                    },
                ),
            confirmPin: yup
                .string()
                .required('Confirm pin is required.')
                .oneOf([yup.ref('pin')], 'pin must match.'),
        })
    const method = useForm<INewAccountContract>({
        defaultValues: account,
        resolver: yupResolver(validateScheme(t)),
    })
    const {
        control,
        handleSubmit,
        watch,
        formState: { errors },
    } = method

    const onSubmit: SubmitHandler<INewAccountContract> = async (data) => {
        onSubmitAccount(data)
    }

    const thisField = (field: {
        label: string
        name: any
        type?: string
        helperText?: string
        handleChange?: (e: any) => void
        isRequire?: boolean
        autoComplete?: string
    }) => {
        return (
            <>
                <OWInput
                    component={WhiteTextField}
                    color="info"
                    fullWidth
                    label={getLabel(field.label, field.isRequire)}
                    control={control}
                    name={field.name}
                    type={field.type ?? 'text'}
                    autoComplete={field.autoComplete}
                />
                {field.helperText && (
                    <FormHelperText>{field.helperText}</FormHelperText>
                )}
            </>
        )

        function getLabel(
            label: string,
            isRequire: boolean | undefined = false,
        ) {
            if (!isRequire) return label
            else return label + ' *'
        }
    }

    const watchPin = watch('pin')

    const displayPinRequirement = () => {
        if ((watchPin === '' || !watchPin.match(/^[0-9]+$/)) && !errors.pin)
            return '6 digit PIN (numbers only)'
        return ''
    }

    const watchPassword = watch('password')
    const displayPasswordRequirement = () => {
        if (
            (watchPassword === '' || !watchPassword.match(PASSWORD_REGEX)) &&
            !errors.password
        )
            return 'at least 8 characters including 1 capital character , 1 special character and 1 alphanumeric characters'
        return ''
    }

    return (
        <form onSubmit={handleSubmit(onSubmit)}>
            <Grid container>
                <Grid item xs={12}>
                    <Grid item container spacing={2} sx={getOverflowSectionSX}>
                        <Grid item container xs={12} spacing={2}>
                            <Grid item xs={6}>
                                {thisField({
                                    label: t('register.label.first_name'),
                                    name: 'firstName',
                                    isRequire: true,
                                    autoComplete: 'given-name',
                                })}
                            </Grid>
                            <Grid item xs={6}>
                                {thisField({
                                    label: t('register.label.last_name'),
                                    name: 'lastName',
                                    isRequire: true,
                                    autoComplete: 'family-name',
                                })}
                            </Grid>
                        </Grid>
                        <Grid xs={12} item>
                            {thisField({
                                label: t('register.label.username'),
                                name: 'userName',
                                isRequire: true,
                                autoComplete: 'username',
                            })}
                        </Grid>
                        <Grid xs={12} item>
                            {thisField({
                                label: t('register.label.email'),
                                name: 'email',
                                isRequire: true,
                                autoComplete: 'email',
                            })}
                        </Grid>
                        <Grid xs={12} md={6} item>
                            {thisField({
                                label: t('register.label.password'),
                                name: 'password',
                                type: 'password',
                                autoComplete: 'new-password',
                                helperText: displayPasswordRequirement(),
                                isRequire: true,
                            })}
                        </Grid>
                        <Grid xs={12} md={6} item>
                            {thisField({
                                label: t('register.label.confirm_password'),
                                name: 'confirmPassword',
                                autoComplete: 'new-password',
                                type: 'password',
                                isRequire: true,
                            })}
                        </Grid>
                        <Grid xs={12} md={6} item>
                            {thisField({
                                label: t('register.label.pin'),
                                name: 'pin',
                                type: 'password',
                                helperText: displayPinRequirement(),
                                isRequire: true,
                            })}
                        </Grid>
                        <Grid xs={12} md={6} item>
                            {thisField({
                                label: t('register.label.confirm_pin'),
                                name: 'confirmPin',
                                type: 'password',
                                isRequire: true,
                            })}
                        </Grid>
                    </Grid>
                    <Button sx={{ display: 'none' }} type="submit" />
                    <Grid item textAlign={'center'}>
                        <NextButton
                            text={'Next'}
                            onClick={handleSubmit(onSubmit)}
                        />
                    </Grid>
                </Grid>
            </Grid>
        </form>
    )
}

export default AdminAccount
